import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useAdminAuth } from "./AdminAuthContext";
import { useEmployeeAuth } from "./EmployeeAuthContext";
import { usePublicSettings } from "../hooks/usePublicSettings";

const MODES = ["light", "dark"];
const MODE_CACHE_KEY = "vj_mode_cache";
const EMPLOYEE_PREF_KEY = "vj_employee_mode_pref";

/** Applies the Light/Dark mode as early/instantly as possible (sets [data-theme] on <html>,
 * consumed by the CSS design tokens in index.css) and caches it for the next cold-start
 * flash-prevention read in public/index.html. Pure UI - never touches business logic. */
export function applyMode(mode) {
  const resolved = MODES.includes(mode) ? mode : "dark";
  document.documentElement.setAttribute("data-theme", resolved);
  try { window.localStorage.setItem(MODE_CACHE_KEY, resolved); } catch (e) {}
  return resolved;
}

const ThemeModeContext = createContext(null);

/** Priority: 1) the signed-in user's own saved preference (member/admin - "default" means
 * "no personal choice made"), 2) Admin's global default_mode, 3) hardcoded "dark" fallback.
 * If Admin disables user_override_enabled, everyone (regardless of personal preference)
 * follows the Admin default - matches the Appearance settings spec exactly. */
export function ThemeModeProvider({ children }) {
  const { member, refresh: refreshMember } = useAuth();
  const { admin, updateAdmin } = useAdminAuth();
  const { employee } = useEmployeeAuth();
  const { data: publicSettings } = usePublicSettings();
  const appearance = publicSettings?.appearance;
  const [mode, setModeState] = useState(() => {
    try { return window.localStorage.getItem(MODE_CACHE_KEY) === "light" ? "light" : "dark"; } catch (e) { return "dark"; }
  });

  let ownPreference = "default";
  if (member?.member?.theme_preference) ownPreference = member.member.theme_preference;
  else if (admin?.theme_preference) ownPreference = admin.theme_preference;
  else if (employee) {
    try { ownPreference = window.localStorage.getItem(EMPLOYEE_PREF_KEY) || "default"; } catch (e) {}
  }

  const overrideEnabled = appearance?.user_override_enabled !== false;
  const defaultMode = appearance?.default_mode === "light" ? "light" : "dark";

  useEffect(() => {
    if (!appearance) return;
    const effective = overrideEnabled && ownPreference !== "default" ? ownPreference : defaultMode;
    setModeState(applyMode(effective));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appearance, ownPreference, overrideEnabled, defaultMode]);

  const setPreference = async (pref) => {
    const effective = overrideEnabled && pref !== "default" ? pref : defaultMode;
    setModeState(applyMode(effective));
    if (member?.member) {
      await api.patch("/member/theme-preference", { theme_preference: pref });
      await refreshMember();
    } else if (admin) {
      const { data } = await api.patch("/admin/auth/theme-preference", { theme_preference: pref });
      updateAdmin(data.admin);
    } else if (employee) {
      try { window.localStorage.setItem(EMPLOYEE_PREF_KEY, pref); } catch (e) {}
    }
  };

  return (
    <ThemeModeContext.Provider value={{ mode, ownPreference, overrideEnabled, defaultMode, setPreference }}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export const useThemeMode = () => useContext(ThemeModeContext);
